/**
 * Standing costs: the schedules, and what each one is waiting for.
 *
 * Shown above the ledger rather than inside it, because they are a different
 * kind of thing. The ledger is money that was spent; this is money that will
 * be, and the whole design rests on never letting the second look like the
 * first. Every figure here is either a plan (labelled as one) or what has
 * actually been recorded — never a projection added to a total.
 */

import { useState, type ReactNode } from 'react';
import { CalendarClock, Check, Pause, Play, SkipForward, Square, Trash2 } from 'lucide-react';
import {
  EXPENSE_FREQUENCY_LABELS,
  EXPENSE_SCHEDULE_STATUS_LABELS,
  type ExpenseScheduleView,
} from '@cenox/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Input,
  Modal,
  Spinner,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api';
import { money } from '../../lib/format';
import {
  useDeleteSchedule,
  useRecordOccurrence,
  useSchedules,
  useSetScheduleStatus,
  useSkipOccurrence,
} from './scheduleData';

export function RecurringExpenses(): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const schedules = useSchedules();
  const [skipping, setSkipping] = useState<ExpenseScheduleView | null>(null);
  const [archiving, setArchiving] = useState<ExpenseScheduleView | null>(null);

  const record = useRecordOccurrence();
  const setStatus = useSetScheduleStatus();
  const remove = useDeleteSchedule();
  const mayManage = can('expenses:update');
  const mayRecord = can('expenses:create');
  const mayDelete = can('expenses:delete');

  if (schedules.isError) {
    return (
      <ErrorState
        message="Could not load recurring expenses."
        onRetry={() => void schedules.refetch()}
      />
    );
  }
  if (schedules.isPending) return <Spinner label="Loading recurring expenses" />;

  const rows = schedules.data.data;
  if (rows.length === 0) return null;

  const due = rows.filter((r) => r.isDue);

  return (
    <Card className="mb-4">
      <CardHeader
        title="Recurring"
        subtitle={
          due.length > 0
            ? `${due.length} waiting to be recorded`
            : 'Standing costs. Nothing is added to your books until it is recorded.'
        }
      />

      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className={`rounded-xl border p-3 ${
              row.isDue ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200'
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                  <CalendarClock className="size-4 shrink-0 text-slate-400" aria-hidden />
                  {row.description ?? row.categoryName ?? 'Recurring expense'}
                  <span className="tabular-nums">{money(row.amount, currency)}</span>
                  {row.status !== 'active' && (
                    <Badge tone="neutral">{EXPENSE_SCHEDULE_STATUS_LABELS[row.status]}</Badge>
                  )}
                  {row.isDue && <Badge tone="warning">Due</Badge>}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {EXPENSE_FREQUENCY_LABELS[row.frequency]}
                  {row.frequency === 'custom' && row.intervalDays
                    ? ` · every ${row.intervalDays} days`
                    : ''}
                  {row.branchName ? ` · ${row.branchName}` : ''}
                  {row.nextDueOn ? ` · next ${row.nextDueOn}` : ' · finished'}
                  {row.endsOn ? ` · until ${row.endsOn}` : ''}
                  {row.autoCreate ? ' · automatic' : ''}
                </p>
                {/*
                  What has really been spent, never what is planned. A reader
                  glancing at this must not come away with a number that
                  includes months nobody has paid.
                */}
                <p className="mt-0.5 text-xs text-slate-400">
                  {row.occurrencesRecorded} recorded · {money(row.totalRecorded, currency)} so far
                  {row.occurrencesSkipped > 0 ? ` · ${row.occurrencesSkipped} skipped` : ''}
                </p>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                {row.status === 'active' && row.nextDueOn && mayRecord && (
                  <Button
                    size="sm"
                    disabled={record.isPending}
                    onClick={() =>
                      // The date we are showing. If the schedule has moved on
                      // since — somebody else, or a second click — the API
                      // refuses rather than quietly taking the next month.
                      record.mutate({ id: row.id, occurrenceOn: row.nextDueOn! })
                    }
                  >
                    <Check className="size-3.5" />
                    Record
                  </Button>
                )}
                {row.status === 'active' && row.nextDueOn && mayManage && (
                  <Button size="sm" variant="ghost" onClick={() => setSkipping(row)}>
                    <SkipForward className="size-3.5" />
                    Skip
                  </Button>
                )}
                {mayManage && row.status === 'active' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setStatus.mutate({ id: row.id, status: 'paused' })}
                  >
                    <Pause className="size-3.5" />
                    Pause
                  </Button>
                )}
                {mayManage && row.status === 'paused' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setStatus.mutate({ id: row.id, status: 'active' })}
                  >
                    <Play className="size-3.5" />
                    Resume
                  </Button>
                )}
                {mayManage && row.status !== 'stopped' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setStatus.mutate({ id: row.id, status: 'stopped' })}
                  >
                    <Square className="size-3.5" />
                    Stop
                  </Button>
                )}
                {/*
                  Only once stopped. Archiving an arrangement the business
                  still has would hide it without ending it, and the next due
                  date would sit there unanswered.
                */}
                {mayDelete && row.status === 'stopped' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => setArchiving(row)}
                  >
                    <Trash2 className="size-3.5" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {record.error instanceof ApiError && (
        <div className="mt-3">
          <ErrorState message={record.error.message} />
        </div>
      )}

      {skipping && <SkipDialog schedule={skipping} onClose={() => setSkipping(null)} />}

      {archiving && (
        <Modal open onClose={() => setArchiving(null)} title="Remove this recurring expense?">
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              It stops appearing in this list. Nothing else changes:{' '}
              <strong>
                the {archiving.occurrencesRecorded} expense
                {archiving.occurrencesRecorded === 1 ? '' : 's'} it already produced
              </strong>{' '}
              stay in your books with their amounts, and your history can still say where they
              came from.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setArchiving(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(archiving.id, { onSuccess: () => setArchiving(null) })
                }
              >
                Remove it
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

/**
 * Skip one occurrence.
 *
 * The reason is required, and it is the only reason this is a dialog rather
 * than a button: a gap in a standing cost is exactly what gets asked about a
 * year later, and "nobody got round to it" and "the office was closed" are
 * different answers.
 */
function SkipDialog({
  schedule,
  onClose,
}: {
  schedule: ExpenseScheduleView;
  onClose: () => void;
}): ReactNode {
  const [reason, setReason] = useState('');
  const skip = useSkipOccurrence();
  const fields = skip.error instanceof ApiError ? skip.error.fields : undefined;

  return (
    <Modal open onClose={onClose} title={`Skip ${schedule.nextDueOn}`}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          skip.mutate({ id: schedule.id, reason }, { onSuccess: onClose });
        }}
      >
        <p className="text-sm text-slate-600">
          Nothing is added to your books. The schedule moves on to the next date, and this gap
          keeps your reason attached to it.
        </p>
        <Input
          label="Why"
          error={fields?.reason?.[0]}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Landlord waived this month"
          autoFocus
        />
        {skip.error && !fields ? <ErrorState message={skip.error.message} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={skip.isPending || reason.trim() === ''}>
            Skip this one
          </Button>
        </div>
      </form>
    </Modal>
  );
}
